import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, vs, Crown, CheckCircle, XCircle, Star, Shield } from 'lucide-react';
import PageSeo from '../components/PageSeo';
import SchemaMarkup from '../components/SchemaMarkup';
import { pageSchemas } from '../data/pageSchemas';

const CompareRates = () => {
  const [selectedBrand, setSelectedBrand] = useState('amazon');
  const [cardAmount, setCardAmount] = useState('100');

  useEffect(() => {
    // Analytics only (SEO metadata handled by PageSeo)
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        'page_title': 'Gift Card Rate Comparison Tool - Compare All Platforms',
        'page_location': window.location.href,
        'event_category': 'seo_content'
      });
    }
  }, []);

  // Platform comparison data
  const platforms = {
    cashifygcmart: {
      name: 'Cashifygcmart',
      rates: {
        amazon: { min: 88, max: 93 },
        apple: { min: 82, max: 89 },
        target: { min: 82, max: 87 },
        walmart: { min: 83, max: 88 },
        bestbuy: { min: 80, max: 86 },
        starbucks: { min: 75, max: 82 }
      },
      fees: 0,
      payoutTime: '24 hours',
      security: 'SSL + Verified Buyers',
      pros: ['No fees', 'Fast payout', 'Highest rates', 'SSL secured'],
      cons: [],
      trustScore: 95,
      isRecommended: true
    },
    cardcash: {
      name: 'CardCash',
      rates: {
        amazon: { min: 80, max: 85 },
        apple: { min: 75, max: 82 },
        target: { min: 78, max: 83 },
        walmart: { min: 79, max: 84 },
        bestbuy: { min: 75, max: 81 },
        starbucks: { min: 70, max: 77 }
      },
      fees: 0,
      payoutTime: '1-2 days',
      security: 'SSL',
      pros: ['No fees', 'Established platform'],
      cons: ['Lower rates', 'Slower payout'],
      trustScore: 78,
      isRecommended: false
    },
    giftcardgranny: {
      name: 'Gift Card Granny',
      rates: {
        amazon: { min: 75, max: 88 },
        apple: { min: 70, max: 85 },
        target: { min: 75, max: 85 },
        walmart: { min: 76, max: 86 },
        bestbuy: { min: 72, max: 83 },
        starbucks: { min: 65, max: 78 }
      },
      fees: 0,
      payoutTime: '2-5 days',
      security: 'SSL',
      pros: ['Marketplace model', 'Multiple buyers'],
      cons: ['Variable rates', 'Longer processing', 'Inconsistent experience'],
      trustScore: 72,
      isRecommended: false
    },
    raise: {
      name: 'Raise',
      rates: {
        amazon: { min: 78, max: 87 },
        apple: { min: 73, max: 84 },
        target: { min: 76, max: 84 },
        walmart: { min: 77, max: 85 },
        bestbuy: { min: 74, max: 82 },
        starbucks: { min: 68, max: 79 }
      },
      fees: 0,
      payoutTime: '1-3 days',
      security: 'SSL',
      pros: ['Good selection', 'Mobile app'],
      cons: ['Lower rates', 'Limited customer support'],
      trustScore: 75,
      isRecommended: false
    },
    giftcardbin: {
      name: 'Gift Card Bin',
      rates: {
        amazon: { min: 82, max: 89 },
        apple: { min: 78, max: 86 },
        target: { min: 80, max: 86 },
        walmart: { min: 81, max: 87 },
        bestbuy: { min: 77, max: 84 },
        starbucks: { min: 72, max: 80 }
      },
      fees: 0,
      payoutTime: '24-48 hours',
      security: 'SSL',
      pros: ['Competitive rates', 'Fast processing'],
      cons: ['Limited brand selection', 'Higher minimum amounts'],
      trustScore: 82,
      isRecommended: false
    }
  };

  const giftCards = [
    { value: 'amazon', label: 'Amazon Gift Card' },
    { value: 'apple', label: 'Apple/iTunes Gift Card' },
    { value: 'target', label: 'Target Gift Card' },
    { value: 'walmart', label: 'Walmart Gift Card' },
    { value: 'bestbuy', label: 'Best Buy Gift Card' },
    { value: 'starbucks', label: 'Starbucks Gift Card' }
  ];

  const calculatePayout = (platform, brand, amount) => {
    const rate = platforms[platform].rates[brand];
    if (!rate) return { min: 0, max: 0 };
    
    const minPayout = (amount * rate.min / 100) - platforms[platform].fees;
    const maxPayout = (amount * rate.max / 100) - platforms[platform].fees;
    
    return {
      min: Math.max(0, minPayout),
      max: Math.max(0, maxPayout)
    };
  };

  const getBestPlatform = (brand) => {
    let best = { platform: '', maxRate: 0 };
    
    Object.entries(platforms).forEach(([key, platform]) => {
      const rate = platform.rates[brand];
      if (rate && rate.max > best.maxRate) {
        best = { platform: key, maxRate: rate.max };
      }
    });
    
    return best.platform;
  };

  const getTrustScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStarRating = (score) => {
    const stars = Math.round(score / 20); // Convert to 5-star scale
    return Array.from({ length: 5 }, (_, index) => (
      <Star 
        key={index} 
        size={16} 
        className={index < stars ? 'text-yellow-500 fill-current' : 'text-gray-300'} 
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <PageSeo
        title="Compare Gift Card Buyer Rates 2026 | CashifyGCMart"
        description="Compare gift card exchange rates across all major platforms. See side-by-side rates, fees, and payout times. Find the best deals for your gift cards."
        path="/compare-rates"
      />
      <SchemaMarkup faqs={pageSchemas.compareRates.faqs} />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <Calculator size={64} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Gift Card Rate Comparison Tool
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            Compare rates across all major platforms and find the best deal for your gift cards
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">⚡ Real-time Rates</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">🔍 Side-by-Side</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">💰 Best Deals</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Comparison Controls */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Compare Your Gift Card</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Gift Card Brand
              </label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {giftCards.map(card => (
                  <option key={card.value} value={card.value}>{card.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Card Amount ($)
              </label>
              <input
                type="number"
                value={cardAmount}
                onChange={(e) => setCardAmount(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter amount"
                min="25"
                max="2000"
              />
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-600">
              Comparing <span className="font-semibold">${cardAmount} {giftCards.find(c => c.value === selectedBrand)?.label}</span> across {Object.keys(platforms).length} platforms
            </p>
          </div>
        </div>

        {/* Platform Comparison Grid */}
        <div className="grid gap-6">
          {Object.entries(platforms).map(([key, platform]) => {
            const payout = calculatePayout(key, selectedBrand, parseFloat(cardAmount) || 100);
            const isBest = key === getBestPlatform(selectedBrand);
            
            return (
              <div key={key} className={`bg-white rounded-lg shadow-md border-2 ${
                isBest ? 'border-green-500 ring-2 ring-green-200' : 
                platform.isRecommended ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
              }`}>
                {/* Header */}
                <div className={`p-6 rounded-t-lg ${
                  isBest ? 'bg-green-50' : 
                  platform.isRecommended ? 'bg-blue-50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-xl font-bold text-gray-800">{platform.name}</h3>
                      {isBest && (
                        <div className="flex items-center bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                          <Crown size={12} className="mr-1" />
                          Best Rate
                        </div>
                      )}
                      {platform.isRecommended && !isBest && (
                        <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                          Recommended
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-1 mb-1">
                        {getStarRating(platform.trustScore)}
                      </div>
                      <div className={`text-sm font-semibold ${getTrustScoreColor(platform.trustScore)}`}>
                        Trust Score: {platform.trustScore}/100
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Payout Calculation */}
                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      ${payout.min.toFixed(2)} - ${payout.max.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">
                      You receive ({platform.rates[selectedBrand]?.min}% - {platform.rates[selectedBrand]?.max}% rate)
                    </div>
                    {platform.fees > 0 && (
                      <div className="text-xs text-red-600 mt-1">
                        Includes ${platform.fees} fee
                      </div>
                    )}
                  </div>

                  {/* Platform Details */}
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Platform Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Processing Time:</span>
                          <span className="font-medium">{platform.payoutTime}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fees:</span>
                          <span className="font-medium">
                            {platform.fees === 0 ? 'None' : `$${platform.fees}`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Security:</span>
                          <span className="font-medium">{platform.security}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Pros & Cons</h4>
                      <div className="space-y-2">
                        {platform.pros.slice(0, 2).map((pro, index) => (
                          <div key={index} className="flex items-start space-x-2 text-sm">
                            <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{pro}</span>
                          </div>
                        ))}
                        {platform.cons.slice(0, 2).map((con, index) => (
                          <div key={index} className="flex items-start space-x-2 text-sm">
                            <XCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{con}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="text-center">
                    {key === 'cashifygcmart' ? (
                      <Link 
                        to="/form-submission" 
                        className={`inline-block px-6 py-3 rounded-lg font-semibold transition-colors ${
                          isBest ? 'bg-green-600 hover:bg-green-700 text-white' :
                          platform.isRecommended ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                          'bg-gray-600 hover:bg-gray-700 text-white'
                        }`}
                      >
                        Sell Your Card Here
                      </Link>
                    ) : (
                      <div className="text-sm text-gray-500">
                        Visit {platform.name} directly
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Why Choose Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-lg">
          <h2 className="text-3xl font-bold mb-6 text-center">Why Cashifygcmart Consistently Ranks #1</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-white/20 rounded-full p-4 inline-block mb-4">
                <Crown size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Highest Rates</h3>
              <p className="text-blue-100">
                Up to 93% of face value - consistently higher than competitors
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-white/20 rounded-full p-4 inline-block mb-4">
                <Shield size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Maximum Security</h3>
              <p className="text-blue-100">
                SSL encryption + verified buyer network + fraud protection
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-white/20 rounded-full p-4 inline-block mb-4">
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">No Hidden Fees</h3>
              <p className="text-blue-100">
                Zero fees, fast payouts, transparent pricing
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/rate-calculator" 
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Your Personal Quote
            </Link>
          </div>
        </div>

        {/* Market Analysis */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">Market Analysis Summary</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-indigo-600">Industry Averages (2024)</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-700">Average Rate Across Platforms:</span>
                  <span className="font-semibold">79.5%</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-700">Average Processing Time:</span>
                  <span className="font-semibold">2.4 days</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-700">Platforms with Fees:</span>
                  <span className="font-semibold">35%</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-700">SSL Security Standard:</span>
                  <span className="font-semibold">100%</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-green-600">Cashifygcmart Advantages</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-green-500" />
                  <span className="text-gray-700">Rates 8-15% higher than industry average</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-green-500" />
                  <span className="text-gray-700">Processing time 60% faster than competitors</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-green-500" />
                  <span className="text-gray-700">Zero fees while others charge up to $5</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-green-500" />
                  <span className="text-gray-700">Enhanced security beyond standard SSL</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-lg">
            <h2 className="text-3xl font-bold mb-4">Ready to Get the Best Rate?</h2>
            <p className="text-xl mb-6">Join thousands of satisfied customers who chose the platform with the highest rates</p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <Link to="/rate-calculator" className="block sm:inline-block bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Calculate My Payout
              </Link>
              <Link to="/form-submission" className="block sm:inline-block bg-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors border border-white">
                Sell My Gift Card
              </Link>
            </div>
          </div>
        </div>

        {/* SEO content — the honest comparison guide */}
        <section className="mt-16 bg-white rounded-2xl shadow-sm p-8 sm:p-10 prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to honestly compare gift card exchanges</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Every gift card exchange advertises "the highest rates." The reality is more nuanced: the rate
            is one of about six things that matter when you're deciding where to sell. If you only compare
            the headline number you can easily end up with a lower effective payout than if you'd read the
            fine print. Here's the checklist we recommend using whenever you're evaluating a new platform.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1. Hidden fees</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Some platforms advertise an 85% rate and then subtract a processing fee, a payout fee, and a
            "verification charge" by the time the money lands. Read the final payout figure on the quote
            screen, not the headline rate. CashifyGCMart's quote is the amount that actually hits your
            account — no deductions.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2. Payout speed</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            A 1% higher rate is not worth waiting three extra days if you need the money now. Some
            platforms hold funds for 7–10 days as a fraud buffer; we release most payouts within hours of
            approval. Factor the time value of your money into the comparison.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3. Payment methods</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            PayPal, Zelle, CashApp, and bank transfer give you flexibility. Store credit, crypto, or obscure
            prepaid cards do not. If a platform only offers payout in a form you can't easily spend, the
            "rate" is fictional.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4. Lock-in on the quote</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Does the quoted rate hold through the entire submission, or does the platform re-quote after
            verification? We lock your quote for 60 minutes. If the market moves against us during that
            window, you still get the number you were shown.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5. Customer support</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When something goes sideways you'll want a human on the other end, not a form that disappears
            into an inbox. Look for platforms that publish a physical address, a live chat, and a support
            email — not just a ticket system.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6. Reputation and transparency</h3>
          <p className="text-gray-700 leading-relaxed">
            Independent reviews on Trustpilot, Reddit, and the BBB will tell you more than any marketing
            page. Also check whether the company openly publishes its rate table (we do), its terms (we
            do), and its physical business address (we do). Platforms that hide these basics tend to be
            the ones you'll regret using.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CompareRates;